export interface RangeRulesResult {
    allowedKMs: string[];
    isRestricted: boolean;
    restrictionMessage: string;
}

/**
 * Returns available KM range options and restriction status based on booking days.
 * The allowedKMs array is sorted numerically ascending so allowedKMs[0] is always the lowest available range.
 * 
 * Rules:
 * - 1 day booking: restricted ONLY for self-drive cars (min 2 days required).
 *   For non-self-drive-car services (bikes, chauffeur, etc.), 1-day bookings are allowed.
 * - 2 days booking: 200, 250, 300 enabled.
 * - 3 or 4 days booking: 150, 200, 250, 300 enabled.
 * - 5 or more days booking: 100 kms enabled along with other ranges (100, 150, 200, 250, 300).
 */
export const getAvailableKMRanges = (
    kmIncluded: string[] = [], 
    totalDays: number,
    isSelfDriveCar: boolean = true
): RangeRulesResult => {
    const days = Math.floor(totalDays);

    if (isSelfDriveCar && days < 2) {
        return {
            allowedKMs: [],
            isRestricted: true,
            restrictionMessage: '1-day bookings are restricted for self-drive cars. Minimum booking duration is 2 days.'
        };
    }

    let allowedRanges: string[] = [];
    if (days < 2) {
        // Non-self-drive-car services (bikes, chauffeur, etc.) allowing 1-day booking
        allowedRanges = ['100', '150', '200', '250', '300'];
    } else if (days === 2) {
        allowedRanges = ['200', '250', '300'];
    } else if (days === 3 || days === 4) {
        allowedRanges = ['150', '200', '250', '300'];
    } else {
        // 5 or more days
        allowedRanges = ['100', '150', '200', '250', '300'];
    }

    // Filter the vehicle's KMIncluded options against allowedRanges
    let filtered = kmIncluded.filter(km => allowedRanges.includes(km));

    // Fallback: If vehicle has unique/fixed range not in standard set, keep original options
    if (filtered.length === 0 && kmIncluded.length > 0) {
        filtered = [...kmIncluded];
    }

    // Sort numerically ascending so the lowest range is always at index 0
    filtered.sort((a, b) => (parseInt(a) || 0) - (parseInt(b) || 0));

    return {
        allowedKMs: filtered,
        isRestricted: false,
        restrictionMessage: ''
    };
};
